import { useEffect, useMemo, useRef, useState } from "react";
import { Form, Formik, FormikProps } from "formik";
import * as Yup from 'yup';
import { Backdrop, Box, CircularProgress, Divider, Grid, IconButton, SelectChangeEvent, Theme, Typography, useTheme } from "@mui/material";
import FormikSearchDropdown from "../../../lib/formik/FormikSearchDropdown";
import MFSelectField from "../../../lib/formik/SelectField";
import { MFSubmitButton } from "../../../lib/formik";
import { RefreshIcon } from "../../../assets/SvgIcons";
import UseRadioGroup from "../../../lib/formik/Radio";
import dayjs from "dayjs";
import { EntityObj, FamilyObj, GenerateRiaFeeResponseBody, GetBillingEntitiesResponseBody, GetFamilyInvoiceInfoResponseBody, GetReportFamiliesListResponseBody, GetReportRmListResponseBody, ReportRmObj, RiaFeeReportStatusObj, UnfreezeInvoiceResponseBody, UpdateRiaFeeReportStatusResponseBody } from "../../../redux-store/types/onBoarding";
import { useDispatch, useSelector } from "react-redux";
import { downloadRiaFeeInvoice, generateRiaFee, getBillingEntities, getFamilyInvoiceInfo, getReportFamiliesList, getReportRmList, unfreezeInvoice, updateRiaFeeReportStatus } from "../../../redux-store/actions/reports";
import { showError } from "../../../redux-store/actions/auth";
import { RootStateType } from "../../../redux-store/reducers";
import { Order, PaginationObj, SortingObj } from "../../investorOverview/types";
import { useSnackbar } from "notistack";
import { CONTENT_TYPE, ROLE_TYPE } from "../../../utils/constant";
import { reportDownload } from "../../Reports/report-utils";
import { CommonLayout } from "../../commonComponents";
import ReportListTable from "./ReportListTable";
import { isModuleAllowed } from "../../../utils/utilityFunctions";
import { useHistory } from "react-router";
import BreadCrum from "../../../lib/BreadCrum";

interface ModifiedReportRmObj extends ReportRmObj {
    label: string;
    value: string;
}

interface ModifiedFamilyObj extends FamilyObj {
    label: string;
    value: string;
}

interface ModifiedEntityObj extends EntityObj {
    label: string;
    value: string;
}

interface Values {
    rm: ModifiedReportRmObj | null;
    familyName: ModifiedFamilyObj | null;
    year: string;
    quarter: string;
    reportType: string;
    investorType: string;
    investor: ModifiedEntityObj | null;
}

const INVESTOR_TYPES = [
    {
        label: "Independent",
        value: "independent"
    },
    {
        label: "Dependent",
        value: "dependent"
    },
    {
        label: "Both",
        value: "both"
    }
]

const REPORT_TYPES = [
    {
        key: "Fee Calculation Report",
        value: "Fee Calculation Report"
    }
]

const QUARTERS = [
    { key: "April-June", value: "Q1" },
    { key: "July-September", value: "Q2" },
    { key: "October-December", value: "Q3" },
    { key: "January-March", value: "Q4" }
]

const startYear = 2025
const currentYear = dayjs().year()

const YEARS: Array<{ key: string; value: string }> = []

for (let year = startYear; year <= currentYear; year++) {
    YEARS.push({
        key: `${year} - ${year + 1}`,
        value: `${year}`
    })
}

const validationSchema = Yup.object().shape({
    rm: Yup
        .object()
        .nullable()
        .test(
            "rm-required",
            "Please select RM",
            function (value) { return !!value }
        ),
    familyName: Yup
        .object()
        .nullable()
        .test(
            "family-name-required",
            "Please select Family Name",
            function (value) { return !!value }
        ),
    year: Yup
        .string()
        .required('Please select Year'),
    quarter: Yup
        .string()
        .required('Please select Quarter'),
    reportType: Yup
        .string()
        .required('Please select Report Type'),
    investorType: Yup.string(),
    investor: Yup
        .object()
        .nullable()
        .test(
            "investor-required",
            "Please select Investor",
            function (value) {
                const { investorType } = this.parent

                if (["dependent", "both"].includes(`${investorType ?? ""}`.toLowerCase())) {
                    return true
                }

                return !!value
            }
        ),
})

export default function RiaReport(
    // {
    //     handleSideSectionDisabled
    // }: {
    //     handleSideSectionDisabled: (value: boolean) => void
    // }
): JSX.Element {
    const { enqueueSnackbar } = useSnackbar();
    const theme = useTheme()
    const dispatch = useDispatch();
    const formikRef = useRef<FormikProps<Values>>(null);

    const history = useHistory();
    const { pathname: pathName } = history.location
    const initialRouter = pathName.split('/').filter((item) => item !== '')

    // Data from Redux
    const { iwappRmId } = useSelector((state: RootStateType) => state.id);
    const {
        userDetails,
        subRole,
        permissions = []
    } = useSelector((store: RootStateType) => store.auth);

    //State
    const [isLoading, setLoading] = useState<boolean>(false)
    const [rmList, setRmList] = useState<Array<ModifiedReportRmObj>>([])
    const [familiesList, setFamiliesList] = useState<Array<ModifiedFamilyObj>>([])
    const [investorList, setInvestorList] = useState<Array<ModifiedEntityObj>>([])
    const [billingEntities, setBillingEntities] = useState<GetBillingEntitiesResponseBody>({} as GetBillingEntitiesResponseBody)
    const [isTableDataLoading, setTableDataLoading] = useState<boolean>(false)
    const [tableData, setTableData] = useState<Array<RiaFeeReportStatusObj>>([])
    const [tablePaginationObj, setTablePaginationObj] = useState<PaginationObj>({
        currentPage: 0,
        rowsPerPage: 5,
        totalRows: 0
    })
    const [tableSortingObj, setTableSortingObj] = useState<SortingObj>({
        order: "desc",
        orderBy: "initiatedAt"
    })
    const [isGenerateReportLoading, setGenerateReportLoading] = useState<boolean>(false)

    const routerPath = (isModuleAllowed({ value: "MFDashboard", compareArr: permissions, objKey: "permissonCode" }) ? [
        {
            name: 'Dashboard',
            path: '/dashboard',
            textSx: { paddingLeft: "0px" }
        },
        {
            name: 'RIA Report',
            path: `/${initialRouter[0]}`,
            isTextLowerCase: false
        }
    ] : [])

    const isSubRoleRex = isModuleAllowed({ value: `${subRole ?? ""}`, compareArr: [ROLE_TYPE.REX] })

    const rexInfoObj = {
        iwappRmId: userDetails?.iwappRmId ?? "",
        name: userDetails?.name ?? "",
        label: `${userDetails?.name ?? ""} ${userDetails?.iwappRmId ? `(${userDetails.iwappRmId})` : ""}`,
        value: userDetails?.iwappRmId ?? "",
    }

    const initialValues: Values = useMemo(() => ({
        rm: isSubRoleRex ? rexInfoObj : null,
        familyName: null,
        year: '',
        quarter: '',
        reportType: 'Fee Calculation Report',
        investorType: "both",
        investor: null,
    }), [isSubRoleRex, userDetails?.iwappRmId, userDetails?.name]);


    const fetchTableData = async ({
        currentPage,
        rowsPerPage,
        sort,
        order,
    }: {
        currentPage: number;
        rowsPerPage: number;
        sort: string;
        order: Order;
    }) => {
        try {
            setTableDataLoading(true)

            const payload = {
                rmId: `${iwappRmId}`,
                sort,
                order: `${order ?? ""}`.toUpperCase(),
                rowsPerPage,
                currentPage
            }

            const {
                count = 0,
                reports = []
            } = await (dispatch(updateRiaFeeReportStatus(payload)) as unknown as Promise<UpdateRiaFeeReportStatusResponseBody>)

            setTableData(reports)
            setTableSortingObj({
                order,
                orderBy: sort
            })
            setTablePaginationObj({
                currentPage: currentPage - 1,
                rowsPerPage,
                totalRows: count
            })
        } catch (error) {
            console.log(error)
        } finally {
            setTableDataLoading(false)
        }
    }

    useEffect(() => {
        fetchTableData({
            currentPage: tablePaginationObj.currentPage + 1,
            rowsPerPage: tablePaginationObj.rowsPerPage,
            sort: tableSortingObj.orderBy,
            order: tableSortingObj.order,
        })
    }, [])

    const fetchFamiliesData = async (searchInput: string) => {
        try {
            if (!formikRef?.current) {
                return
            }

            const { values } = formikRef.current

            // if (values.familyName) {
            //     return;
            // }

            if (`${searchInput ?? ""}`.trim().length !== 0) {

                const payload = {
                    rmId: values?.rm?.iwappRmId,
                    searchInput: `${searchInput ?? ""}`.trim()
                }
                const response = await (dispatch(getReportFamiliesList(payload)) as unknown as Promise<GetReportFamiliesListResponseBody>);

                if (!response?.isSuccess) {
                    dispatch(showError(response?.message || "Data not found"))
                    setFamiliesList([])
                    return
                }

                if (response?.isSuccess) {

                    const modifiedFamiliesList = (response?.families ?? []).map((obj: FamilyObj) => ({
                        ...obj,
                        label: `${obj?.family_name ?? ""} ${obj?.family_id ? `(${obj.family_id})` : ''}`,
                        value: obj?.family_id
                    }))

                    setFamiliesList(modifiedFamiliesList)
                }
            } else {
                setFamiliesList([])
            }
        } catch (error) {
            console.log(error)
        }
    }

    const fetchRmListData = async (searchInput: string) => {
        try {
            if (!formikRef?.current) {
                return
            }

            // const { values } = formikRef.current

            // if (values.rm) {
            //     return;
            // }

            if (`${searchInput ?? ""}`.trim().length !== 0) {

                const payload = {
                    searchInput: `${searchInput ?? ""}`.trim()
                }
                const response = await (dispatch(getReportRmList(payload)) as unknown as Promise<GetReportRmListResponseBody>);

                if (!response) {
                    setRmList([])

                    return
                }

                if (response) {

                    const modifiedRmList = (response?.rms ?? []).map((obj: ReportRmObj) => ({
                        ...obj,
                        label: `${obj?.name ?? ""} ${obj?.iwappRmId ? `(${obj.iwappRmId})` : ""}`,
                        value: obj?.iwappRmId
                    }))

                    setRmList(modifiedRmList)
                }
            } else {
                setRmList([])
            }
        } catch (error) {
            console.log(error)
        }
    }

    const fetchInvestorsData = async ({
        familyName,
        year,
        quarter
    }: {
        familyName: ModifiedFamilyObj | null,
        year: string,
        quarter: string
    }) => {
        try {
            if (familyName && year && quarter) {
                setLoading(true)

                const payload = {
                    familyId: familyName?.family_id,
                    year: Number(year),
                    quarter
                }

                const response = await (dispatch(getBillingEntities(payload)) as unknown as Promise<GetBillingEntitiesResponseBody>);

                setBillingEntities(response)

                if (!response?.isSuccess) {
                    dispatch(showError(response?.message || "Data not found"))
                    setInvestorList([])
                    return
                }

                if (response?.isSuccess) {
                    const modifiedIndependentEntityList = (response?.independentEntityList ?? []).map((obj: EntityObj) => ({
                        ...obj,
                        label: `${obj?.billing_entity_name ?? ""} ${obj?.billing_entity_id ? `(${obj.billing_entity_id})` : ""}`,
                        value: obj?.billing_entity_id
                    }))

                    setInvestorList(modifiedIndependentEntityList)
                }
            }
        } catch (error) {
            console.log(error)
        } finally {
            setLoading(false)
        }
    }

    const handleRmChange = async (selectedObj: Record<string, any>) => {
        if (!formikRef?.current) {
            return
        }

        const { values, setValues } = formikRef.current

        setValues({
            ...values,
            rm: selectedObj as ModifiedReportRmObj,
            familyName: null,
            year: '',
            quarter: '',
            investor: null,
        })

        setFamiliesList([])
        setInvestorList([])
        setBillingEntities({} as GetBillingEntitiesResponseBody)
    }

    const handleFamilyChange = async (selectedObj: Record<string, any>) => {
        if (!formikRef?.current) {
            return
        }

        const { values, setValues } = formikRef.current

        setValues({
            ...values,
            familyName: selectedObj as ModifiedFamilyObj,
            year: '',
            quarter: '',
            investor: null,
            investorType: ((`${selectedObj?.family_name ?? ""}`.toLowerCase() === "all") ? "both" : values.investorType)
        })

        setInvestorList([])
        setBillingEntities({} as GetBillingEntitiesResponseBody)
    }

    const handleYearChange = ({ target: { value } }: SelectChangeEvent<unknown>) => {
        if (!formikRef?.current) {
            return
        }

        const { values, setValues } = formikRef.current

        setValues({
            ...values,
            year: value as string,
            investor: null
        })

        setInvestorList([])
        setBillingEntities({} as GetBillingEntitiesResponseBody)

        fetchInvestorsData({ familyName: values.familyName, year: value as string, quarter: values.quarter })
    }

    const handleQuarterChange = ({ target: { value } }: SelectChangeEvent<unknown>) => {
        if (!formikRef?.current) {
            return
        }

        const { values, setValues } = formikRef.current

        setValues({
            ...values,
            quarter: value as string,
            investor: null
        })

        setInvestorList([])
        setBillingEntities({} as GetBillingEntitiesResponseBody)

        fetchInvestorsData({ familyName: values.familyName, year: values.year, quarter: value as string })
    }

    const handleInvestorTypeChange = ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
        if (!formikRef?.current) {
            return
        }

        const { setValues, values } = formikRef.current

        setValues({
            ...values,
            investorType: value,
            investor: null
        })
    }

    const handleRefresh = async () => {
        await fetchTableData({
            currentPage: 1,
            rowsPerPage: tablePaginationObj.rowsPerPage,
            sort: tableSortingObj.orderBy,
            order: tableSortingObj.order,
        })
    }

    const handleSubmit = async (values: Values) => {
        try {
            setGenerateReportLoading(true)
            // handleSideSectionDisabled(true)

            // billingEntityId based on investorType selection:
            // independent -> investor's billing_entity_id, dependent -> null, both -> null.
            // Intentional contract change: for the "dependent" case this is now null
            // (previously the family_id). Dependent investors are scoped by family via
            // familyIds instead, so the family id is no longer sent in the billing-entity slot.
            const billingEntityIdMap: Record<string, string | null | undefined> = {
                independent: values?.investor?.billing_entity_id,
                dependent: null,
                both: null
            }

            const familyId = values?.familyName?.family_id
            const billingEntityId = billingEntityIdMap[`${values?.investorType ?? ""}`.toLowerCase()]
            // rmIds from the RM filter: "all" -> null, a specific RM -> [that rm's iwappRmId].
            // Intentional contract change: the "all" case now sends null (previously the
            // logged-in RM id). Scoping for "all" relies on loggedinUser in the payload below.
            const rmId = (`${values?.rm?.name ?? ""}`.toLowerCase() === "all") ? null : values?.rm?.iwappRmId

            const familyIds = [familyId].filter(Boolean).map((id) => `${id}`)
            const billingEntityIds = [billingEntityId].filter(Boolean).map((id) => `${id}`)
            const rmIds = [rmId].filter(Boolean).map((id) => `${id}`)

            const payload = {
                familyIds: familyIds.length ? familyIds : null,
                year: Number(values?.year),
                quarter: values?.quarter,
                billingEntityIds: billingEntityIds.length ? billingEntityIds : null,
                rmIds: rmIds.length ? rmIds : null,
                // familyName: values?.familyName?.family_name,
                // billingEntityName: billingEntityNameMap[`${values?.investorType ?? ""}`.toLowerCase()],
                investorType: values?.investorType,
                invoiceState: null,
                familyHead: billingEntities?.family_head,
                loggedinUser: `${iwappRmId}`,
                // rmName: values?.rm?.name
            }

            const response = await (dispatch(generateRiaFee(payload)) as unknown as Promise<GenerateRiaFeeResponseBody>)

            if (response?.isSuccess) {
                enqueueSnackbar(`${response?.message ?? ""}`, {
                    variant: 'success',
                    autoHideDuration: 5000,
                });

                await handleRefresh()

                return
            }
            if (!response?.isSuccess) {
                enqueueSnackbar(`${response?.message ?? ""}`, {
                    variant: 'error',
                    autoHideDuration: 5000,
                });

                return
            }
        } catch (error) {
            console.log(error)
        } finally {
            setGenerateReportLoading(false)
            // handleSideSectionDisabled(false)
        }
    }

    const handleTableSort = async (orderBy: string, order: Order) => {
        await fetchTableData({
            currentPage: 1,
            rowsPerPage: tablePaginationObj.rowsPerPage,
            sort: orderBy,
            order,
        })
    }

    const handleTableRowsPerPageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        await fetchTableData({
            currentPage: 1,
            rowsPerPage: Number(event.target.value),
            sort: tableSortingObj.orderBy,
            order: tableSortingObj.order,
        })
    }

    const handleTablePageChange = async (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
        await fetchTableData({
            currentPage: page + 1,
            rowsPerPage: tablePaginationObj.rowsPerPage,
            sort: tableSortingObj.orderBy,
            order: tableSortingObj.order,
        })
    }

    const handleUnfreezeInvoice = async (currentRow: RiaFeeReportStatusObj) => {
        try {
            setLoading(true)

            const payload = {
                familyId: currentRow?.familyId,
                year: Number(currentRow?.year),
                quarter: currentRow?.quarter,
                billingEntityId: currentRow?.billingEntityId,
                rmId: `${iwappRmId}`,
                jobId: Number(currentRow?.jobId)
            }

            const response = await (dispatch(unfreezeInvoice(payload)) as unknown as Promise<UnfreezeInvoiceResponseBody>)

            if (response?.isSuccess) {
                enqueueSnackbar(`${response?.message ?? ""}`, {
                    variant: 'success',
                    autoHideDuration: 5000,
                });

                return true
            }
            if (!response?.isSuccess) {
                enqueueSnackbar(`${response?.message ?? ""}`, {
                    variant: 'error',
                    autoHideDuration: 5000,
                });

                return false
            }
        } catch (error) {
            console.log(error)
        } finally {
            setLoading(false)
        }
    }

    const handleShareInvoice = async (currentRow: RiaFeeReportStatusObj) => {
        try {
            setLoading(true)

            const payload = {
                familyId: currentRow?.familyId,
                year: Number(currentRow?.year),
                quarter: currentRow?.quarter,
                billingEntityId: currentRow?.billingEntityId,
                investorEmail: currentRow?.investorEmail,
                rmEmail: currentRow?.rmEmail,
                id: Number(currentRow?.id)
            }

            const response = await (dispatch(getFamilyInvoiceInfo(payload)) as unknown as Promise<GetFamilyInvoiceInfoResponseBody>)

            if (response?.isSuccess) {
                enqueueSnackbar(`${response?.message ?? ""}`, {
                    variant: 'success',
                    autoHideDuration: 5000,
                });

                return
            }
            if (!response?.isSuccess) {
                enqueueSnackbar(`${response?.message ?? ""}`, {
                    variant: 'error',
                    autoHideDuration: 5000,
                });

                return
            }
        } catch (error) {
            console.log(error)
        } finally {
            setLoading(false)
        }
    }

    const handleDownloadInvoice = async (currentRow: RiaFeeReportStatusObj) => {
        try {
            setLoading(true)

            const payload = {
                familyId: currentRow?.familyId,
                year: Number(currentRow?.year),
                quarter: currentRow?.quarter,
                billingEntityId: currentRow?.billingEntityId,
                investorEmail: currentRow?.investorEmail,
                rmEmail: currentRow?.rmEmail,
                id: Number(currentRow?.id)
            }

            const response = await (dispatch(downloadRiaFeeInvoice(payload)) as unknown as Promise<any>)

            if (response && [CONTENT_TYPE.PDF].includes(response.type)) {
                enqueueSnackbar('File downloaded successfully.', {
                    variant: 'success',
                    autoHideDuration: 2000,
                });

                reportDownload(response as unknown as Blob, `Invoice-${currentRow?.billingEntityId}.pdf`);
            } else {
                enqueueSnackbar('File download failed!.', {
                    variant: 'error',
                    autoHideDuration: 2000,
                });
            }

        } catch (error) {
            console.log(error)
        } finally {
            setLoading(false)
        }
    }

    const handleMenuItemClick = async (selectedMenuItem: string, currentRow: RiaFeeReportStatusObj) => {
        switch (`${selectedMenuItem}`.toLowerCase()) {
            case "unfreeze": {
                const response = await handleUnfreezeInvoice(currentRow)

                if (response) {
                    await fetchTableData({
                        currentPage: tablePaginationObj.currentPage + 1,
                        rowsPerPage: tablePaginationObj.rowsPerPage,
                        sort: tableSortingObj.orderBy,
                        order: tableSortingObj.order,
                    })
                }

                break;
            }
            case "download report": {
                if (currentRow?.reportUrl) {
                    window.open(currentRow.reportUrl, "_blank")
                }
                break;
            }
            case "share invoice": {
                await handleShareInvoice(currentRow)
                break;
            }
            case "download invoice": {
                await handleDownloadInvoice(currentRow)
                break;
            }
        }
    }

    return (
        // CommonLayout will be removed in case of Reports Layout
        <CommonLayout>
            <>
                <Backdrop
                    sx={(theme: Theme) => ({
                        color: theme.palette.common.white,
                        zIndex: theme.zIndex.drawer + 1
                    })}
                    open={isLoading}
                >
                    <CircularProgress sx={(theme: Theme) => ({ color: theme.palette.primary[500] })} />
                </Backdrop>

                {/* <ReportsRightLayout > */}

                <Box sx={{ marginBottom: "0.5em" }}>
                    <BreadCrum array={routerPath} />
                </Box>

                <Typography
                    variant="h1"
                    // variant="h2"
                    sx={(theme: Theme) => ({
                        // color: 'text.primary',
                        // lineHeight: '25px',
                        // letterSpacing: '0.01px',
                        lineHeight: "32.5px",
                        color: theme.palette.text.primary,
                        mb: '40px',
                    })}
                >
                    RIA Report
                </Typography>

                <>
                    {/* Below Box container will be removed in case of Reports Layout */}
                    <Box
                        sx={(theme: Theme) => ({
                            borderRadius: "25px",
                            padding: "20px 25px 30px 25px",
                            backgroundColor: theme.palette.common.white,
                            boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`
                        })}
                    >
                        <Formik
                            innerRef={formikRef}
                            validationSchema={validationSchema}
                            initialValues={initialValues}
                            onSubmit={handleSubmit}
                        >
                            {({ values }) => (
                                <Form>
                                    <Grid container columnSpacing={3} rowSpacing={2}>
                                        <Grid item xs={12} lg={6}>
                                            <FormikSearchDropdown
                                                name="rm"
                                                label="RM *"
                                                options={(isSubRoleRex ? [rexInfoObj] : (rmList ?? []))}
                                                fetchData={fetchRmListData}
                                                onChange={handleRmChange}
                                                filterOptions={(x) => x}
                                                onOpen={() => {
                                                    if (!formikRef?.current?.values?.rm) {
                                                        setRmList([]);
                                                    }
                                                }}
                                                disabled={isGenerateReportLoading || isSubRoleRex}
                                            />
                                        </Grid>
                                        <Grid item xs={12} lg={6}>
                                            <FormikSearchDropdown
                                                name="familyName"
                                                label="Family Name *"
                                                options={familiesList ?? []}
                                                fetchData={fetchFamiliesData}
                                                onChange={handleFamilyChange}
                                                filterOptions={(x) => x}
                                                onOpen={() => {
                                                    if (!formikRef?.current?.values?.familyName) {
                                                        setFamiliesList([]);
                                                    }
                                                }}
                                                disabled={isGenerateReportLoading || !values.rm}
                                            />
                                        </Grid>
                                        <Grid item xs={12} lg={6}>
                                            <MFSelectField
                                                name='year'
                                                label="Financial Year *"
                                                items={YEARS}
                                                onChange={handleYearChange}
                                                disabled={isGenerateReportLoading}
                                            />
                                        </Grid>
                                        <Grid item xs={12} lg={6}>
                                            <MFSelectField
                                                name='quarter'
                                                label="Quarter *"
                                                items={QUARTERS}
                                                onChange={handleQuarterChange}
                                                disabled={isGenerateReportLoading}
                                            />
                                        </Grid>
                                        {/* <Grid item xs={12} lg={6}>
                                            <MFSelectField
                                                name='reportType'
                                                label="Report Type *"
                                                items={REPORT_TYPES}
                                                disabled
                                            />
                                        </Grid> */}
                                        <Grid item xs={12} lg={6}>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    rowGap: "20px",
                                                }}
                                            >
                                                <Typography variant="body_500">Investor Type *</Typography>
                                                <UseRadioGroup
                                                    labelColor={theme.palette.text.secondary}
                                                    name="investorType"
                                                    items={INVESTOR_TYPES}
                                                    onChange={handleInvestorTypeChange}
                                                    disabled={
                                                        isGenerateReportLoading ||
                                                        (`${values?.familyName?.family_name ?? ""}`.toLowerCase() === "all")
                                                    }
                                                />
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} lg={6} sx={{ display: { xs: "none", lg: "block" } }} />
                                        <Grid item xs={12} lg={6} >
                                            {["independent"].includes(`${values?.investorType ?? ""}`.toLowerCase()) && (
                                                < Box
                                                    sx={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                    }}
                                                >
                                                    <Box sx={{ height: { xs: "20px", lg: "24px" } }} />
                                                    <FormikSearchDropdown
                                                        name='investor'
                                                        label='Select Investor *'
                                                        options={investorList}
                                                        disabled={isGenerateReportLoading}
                                                    />
                                                </Box>
                                            )}
                                        </Grid>
                                        <Grid item xs={6} sx={{ display: { xs: "none", lg: "block" } }} />
                                        <Grid item xs={7} />
                                        <Grid item xs={5} mt={3.5}>
                                            <MFSubmitButton
                                                label="Initiate"
                                                loadingEnable={isGenerateReportLoading}
                                            />
                                        </Grid>
                                    </Grid >
                                </Form >
                            )}
                        </Formik >
                    </Box>
                    <Divider
                        sx={(theme: Theme) => ({
                            mt: 4,
                            mb: 4,
                            borderColor: theme.palette.grey[500]
                        })}
                    />
                    <Box
                        sx={(theme: Theme) => ({
                            borderRadius: "25px",
                            padding: "10px 25px 30px 25px",
                            backgroundColor: theme.palette.common.white,
                            boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`
                        })}
                    >
                        <Box sx={{ marginTop: 1 }}>
                            <Box>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        columnGap: "10px"
                                    }}
                                >
                                    <Typography
                                        variant='body_600'
                                        sx={(theme: Theme) => ({
                                            color: theme.palette.text.primary
                                        })}
                                    >
                                        Report List
                                    </Typography>
                                    <IconButton
                                        sx={{
                                            cursor: 'pointer',
                                            "&: disabled": {
                                                opacity: 0.5
                                            },
                                        }}
                                        onClick={handleRefresh}
                                        disabled={isTableDataLoading}
                                    >
                                        <RefreshIcon />
                                    </IconButton>
                                </Box>

                                <Box sx={{ marginTop: 5 }}>
                                    <ReportListTable
                                        isLoading={isTableDataLoading}
                                        tableData={tableData}
                                        tableSortingObj={tableSortingObj}
                                        handleTableSort={handleTableSort}
                                        tablePaginationObj={tablePaginationObj}
                                        handleTableRowsPerPageChange={handleTableRowsPerPageChange}
                                        handleTablePageChange={handleTablePageChange}
                                        handleMenuItemClick={handleMenuItemClick}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                    {/* </ReportsRightLayout > */}
                </>
            </>
        </CommonLayout>
    );
}



